import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { 
  BarChart, Bar, LineChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { 
  Fuel, TrendingUp, TrendingDown, AlertTriangle, DollarSign, 
  Users, Droplets, ArrowUp, ArrowDown, Calendar,
  Wallet, PiggyBank, CircleDollarSign, BatteryCharging, Package
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: '12months', label: '12 Months' },
  { value: 'year', label: 'This Year' }
];

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState('week');
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [eChargingData, setEChargingData] = useState(null);
  const [lubricantsData, setLubricantsData] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [chartPeriod]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      // Fetch E-Charging and Lubricants data if enabled
      if (res.data?.e_charging?.enabled) {
        fetchEChargingData();
      }
      if (res.data?.lubricants?.enabled) {
        fetchLubricantsData();
      }
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const fetchEChargingData = async () => {
    try {
      const res = await axios.get(`${API}/e-charging/dashboard`);
      setEChargingData(res.data);
    } catch (error) {
      console.error('Failed to load E-Charging data');
    }
  };

  const fetchLubricantsData = async () => {
    try {
      const res = await axios.get(`${API}/lubricants/dashboard`);
      setLubricantsData(res.data);
    } catch (error) {
      console.error('Failed to load Lubricants data');
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/comprehensive`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    setChartLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/chart-trends?period=${chartPeriod}`);
      setChartData(res.data);
    } catch (error) {
      console.error('Failed to load chart data', error);
    } finally {
      setChartLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `₹${(value || 0).toLocaleString('en-IN')}`;
  };

  const formatNumber = (value) => {
    return (value || 0).toLocaleString('en-IN');
  };

  // Period selector component
  const PeriodSelector = () => (
    <div className="flex gap-1 bg-secondary/50 p-1 rounded-lg">
      {PERIOD_OPTIONS.map(option => (
        <button
          key={option.value}
          onClick={() => setChartPeriod(option.value)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            chartPeriod === option.value
              ? 'bg-primary text-white shadow-sm'
              : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  // Get chart data - use chartData if available, fallback to data for initial load
  const getSalesTrend = () => chartData?.sales_trend || data?.sales_trend || [];
  const getRevenueTrend = () => chartData?.revenue_trend || data?.revenue_trend || [];
  const getLossTrend = () => chartData?.loss_trend || data?.loss_trend || [];
  const getCreditTrend = () => chartData?.credit_collection_trend || data?.credit_collection_trend || [];

  const getPeriodLabel = () => {
    const labels = {
      'week': 'Last 7 Days',
      'month': 'Last 30 Days',
      '12months': 'Last 12 Months',
      'year': 'This Year'
    };
    return labels[chartPeriod] || 'Last 7 Days';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted">Loading dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  // Stock gauge component
  const StockGauge = ({ label, current, capacity, percent, color, fuelType }) => (
    <div className="text-center">
      <div className="relative w-24 h-24 mx-auto mb-2">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            className="text-muted/20"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke={color}
            strokeWidth="8"
            fill="transparent"
            strokeDasharray={`${percent * 2.51} 251`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold">{percent}%</span>
        </div>
      </div>
      <p className="font-semibold text-sm">{label}</p>
      <p className="text-xs text-muted">{formatNumber(current)}L / {formatNumber(capacity)}L</p>
      {percent < 20 && (
        <span className="inline-flex items-center text-xs text-red-600 mt-1">
          <ArrowDown className="w-3 h-3 mr-1" /> Low
        </span>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page" className="space-y-6">
        {/* Header Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Yesterday Revenue */}
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-700 dark:text-emerald-400">Yesterday's Revenue</p>
                <p className="text-xl font-bold text-emerald-800 dark:text-emerald-300">
                  {formatCurrency(data?.revenue?.yesterday)}
                </p>
              </div>
            </div>
          </Card>

          {/* This Month Revenue */}
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-700 dark:text-blue-400">This Month Revenue</p>
                <p className="text-xl font-bold text-blue-800 dark:text-blue-300">
                  {formatCurrency(data?.revenue?.this_month)}
                </p>
              </div>
            </div>
          </Card>

          {/* Credit Outstanding */}
          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-700 dark:text-amber-400">Credit Outstanding</p>
                <p className="text-xl font-bold text-amber-800 dark:text-amber-300">
                  {formatCurrency(data?.outstanding?.total)}
                </p>
              </div>
            </div>
          </Card>

          {/* Alerts */}
          <Card className={`p-4 ${data?.alerts?.unread_count > 0 
            ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200' 
            : 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${data?.alerts?.unread_count > 0 ? 'bg-red-500' : 'bg-gray-400'}`}>
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className={`text-xs ${data?.alerts?.unread_count > 0 ? 'text-red-700 dark:text-red-400' : 'text-gray-600'}`}>
                  Unread Alerts
                </p>
                <p className={`text-xl font-bold ${data?.alerts?.unread_count > 0 ? 'text-red-800 dark:text-red-300' : ''}`}>
                  {data?.alerts?.unread_count || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tank Levels & Today Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tank Levels */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Droplets className="w-5 h-5 text-blue-500" />
                Tank Levels
              </h3>
              {(data?.stock?.ms?.percent < 20 || data?.stock?.hsd?.percent < 20) && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Low Stock
                </span>
              )}
            </div>
            <div className="flex justify-around">
              <StockGauge 
                label="MS (Petrol)" 
                current={data?.stock?.ms?.current} 
                capacity={data?.stock?.ms?.capacity}
                percent={data?.stock?.ms?.percent}
                color="#f59e0b"
                fuelType="MS"
              />
              <StockGauge 
                label="HSD (Diesel)" 
                current={data?.stock?.hsd?.current} 
                capacity={data?.stock?.hsd?.capacity}
                percent={data?.stock?.hsd?.percent}
                color="#3b82f6"
                fuelType="HSD"
              />
            </div>
          </Card>

          {/* Today's Sales */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Today's Sales
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div>
                  <p className="text-sm text-muted">MS (Petrol)</p>
                  <p className="text-2xl font-bold text-amber-600">{formatNumber(data?.today_sales?.ms)}L</p>
                </div>
                <Fuel className="w-8 h-8 text-amber-400" />
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div>
                  <p className="text-sm text-muted">HSD (Diesel)</p>
                  <p className="text-2xl font-bold text-blue-600">{formatNumber(data?.today_sales?.hsd)}L</p>
                </div>
                <Fuel className="w-8 h-8 text-blue-400" />
              </div>
            </div>
          </Card>

          {/* Today's Loss */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-500" />
              Today's Loss
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div>
                  <p className="text-sm text-muted">MS Loss</p>
                  <p className={`text-2xl font-bold ${data?.today_loss?.ms > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatNumber(data?.today_loss?.ms)}L
                  </p>
                </div>
                {data?.today_loss?.ms > 0 ? (
                  <ArrowDown className="w-8 h-8 text-red-400" />
                ) : (
                  <span className="text-green-500 text-sm">OK</span>
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div>
                  <p className="text-sm text-muted">HSD Loss</p>
                  <p className={`text-2xl font-bold ${data?.today_loss?.hsd > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatNumber(data?.today_loss?.hsd)}L
                  </p>
                </div>
                {data?.today_loss?.hsd > 0 ? (
                  <ArrowDown className="w-8 h-8 text-red-400" />
                ) : (
                  <span className="text-green-500 text-sm">OK</span>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Row - Period Selector */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Performance Trends</h2>
          <PeriodSelector />
        </div>

        {/* Charts Row 1: Sales Trend & Revenue Trend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Trend */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary" />
              Sales Trend ({getPeriodLabel()})
              {chartLoading && <span className="text-xs text-muted-foreground ml-2">Loading...</span>}
            </h3>
            {getSalesTrend().length > 0 ? (
              <ResponsiveContainer width="100%" height={188}>
                <AreaChart data={getSalesTrend()}>
                  <defs>
                    <linearGradient id="colorMs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorHsd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={chartPeriod === '12months' || chartPeriod === 'year' ? 0 : 'preserveStartEnd'} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip formatter={(value) => `${value.toLocaleString()}L`} />
                  <Legend />
                  <Area type="monotone" dataKey="ms" name="MS" stroke="#f59e0b" fillOpacity={1} fill="url(#colorMs)" />
                  <Area type="monotone" dataKey="hsd" name="HSD" stroke="#3b82f6" fillOpacity={1} fill="url(#colorHsd)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[188px] flex items-center justify-center text-muted">No sales data available</div>
            )}
          </Card>

          {/* Revenue Trend */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <CircleDollarSign className="w-5 h-5 text-green-500" />
              Revenue Trend ({getPeriodLabel()})
              {chartLoading && <span className="text-xs text-muted-foreground ml-2">Loading...</span>}
            </h3>
            {getRevenueTrend().length > 0 ? (
              <ResponsiveContainer width="100%" height={188}>
                <AreaChart data={getRevenueTrend()}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={chartPeriod === '12months' || chartPeriod === 'year' ? 0 : 'preserveStartEnd'} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#22c55e" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[188px] flex items-center justify-center text-muted">No revenue data available</div>
            )}
          </Card>
        </div>

        {/* Charts Row 2: Loss Trend & Credit Collection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Loss Trend */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-500" />
              Loss Trend ({getPeriodLabel()})
              {chartLoading && <span className="text-xs text-muted-foreground ml-2">Loading...</span>}
            </h3>
            {getLossTrend().length > 0 ? (
              <ResponsiveContainer width="100%" height={188}>
                <LineChart data={getLossTrend()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={chartPeriod === '12months' || chartPeriod === 'year' ? 0 : 'preserveStartEnd'} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => `${value}L`} />
                  <Legend />
                  <Line type="monotone" dataKey="ms" name="MS Loss" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="hsd" name="HSD Loss" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[188px] flex items-center justify-center text-muted">No loss data available</div>
            )}
          </Card>

          {/* Credit Collection Trend */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <PiggyBank className="w-5 h-5 text-purple-500" />
              Credit Collection ({getPeriodLabel()})
              {chartLoading && <span className="text-xs text-muted-foreground ml-2">Loading...</span>}
            </h3>
            {getCreditTrend().length > 0 ? (
              <ResponsiveContainer width="100%" height={188}>
                <BarChart data={getCreditTrend()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={chartPeriod === '12months' || chartPeriod === 'year' ? 0 : 'preserveStartEnd'} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="amount" name="Collected" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[188px] flex items-center justify-center text-muted">No collection data available</div>
            )}
          </Card>
        </div>

        {/* Bottom Row: Outstanding & Monthly Sales */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Outstanding Customers */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-amber-500" />
              Top 5 Outstanding Customers
            </h3>
            <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-400">Total Outstanding</p>
              <p className="text-2xl font-bold text-amber-800 dark:text-amber-300">{formatCurrency(data?.outstanding?.total)}</p>
            </div>
            {data?.outstanding?.customers?.length > 0 ? (
              <div className="space-y-2">
                {data.outstanding.customers.slice(0, 5).map((customer, idx) => (
                  <div key={customer.id} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-medium">
                        {idx + 1}
                      </span>
                      <span className="truncate max-w-[150px]">{customer.firm_name}</span>
                    </div>
                    <span className="font-semibold text-amber-600">{formatCurrency(customer.balance)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-4">No outstanding dues</div>
            )}
          </Card>

          {/* This Month Sales */}
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <Fuel className="w-5 h-5 text-green-500" />
              This Month Sales
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">MS (Petrol)</span>
                  <Fuel className="w-5 h-5 text-amber-500" />
                </div>
                <p className="text-2xl font-bold text-amber-800 dark:text-amber-300">
                  {formatNumber(data?.month_sales?.ms?.qty)}L
                </p>
                <p className="text-sm text-amber-600">{formatCurrency(data?.month_sales?.ms?.value)}</p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-400">HSD (Diesel)</span>
                  <Fuel className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-blue-800 dark:text-blue-300">
                  {formatNumber(data?.month_sales?.hsd?.qty)}L
                </p>
                <p className="text-sm text-blue-600">{formatCurrency(data?.month_sales?.hsd?.value)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* E-Charging & Lubricants Section */}
        {(settings?.e_charging?.enabled || settings?.lubricants?.enabled) && (
          <>
            {/* Section Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Additional Revenue Streams</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* E-Charging Stats */}
              {settings?.e_charging?.enabled && eChargingData && (
                <Card className="p-6">
                  <h3 className="font-semibold flex items-center gap-2 mb-4">
                    <BatteryCharging className="w-5 h-5 text-yellow-500" />
                    E-Charging Station
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <p className="text-xs text-yellow-700 dark:text-yellow-400">Today Revenue</p>
                      <p className="text-xl font-bold text-yellow-800 dark:text-yellow-300">
                        {formatCurrency(eChargingData?.today?.revenue)}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-xs text-green-700 dark:text-green-400">Month Revenue</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-300">
                        {formatCurrency(eChargingData?.month?.revenue)}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className="text-xs text-purple-700 dark:text-purple-400">Month Profit</p>
                      <p className="text-xl font-bold text-purple-800 dark:text-purple-300">
                        {formatCurrency(eChargingData?.month?.profit)}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-blue-700 dark:text-blue-400">Energy (Month)</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-300">
                        {eChargingData?.month?.energy_kwh || 0} kWh
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Sessions (Month)</span>
                      <span className="font-medium">{eChargingData?.month?.sessions || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Hours</span>
                      <span className="font-medium">{eChargingData?.month?.duration_hours || 0} hrs</span>
                    </div>
                  </div>
                </Card>
              )}

              {/* Lubricants Stats */}
              {settings?.lubricants?.enabled && lubricantsData && (
                <Card className="p-6">
                  <h3 className="font-semibold flex items-center gap-2 mb-4">
                    <Package className="w-5 h-5 text-blue-500" />
                    Lubricants
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-blue-700 dark:text-blue-400">Inventory Value</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-300">
                        {formatCurrency(lubricantsData?.inventory?.cost_value)}
                      </p>
                      <p className="text-xs text-blue-600">{lubricantsData?.inventory?.total_items || 0} items</p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-xs text-green-700 dark:text-green-400">Today Sales</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-300">
                        {formatCurrency(lubricantsData?.today?.revenue)}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className="text-xs text-purple-700 dark:text-purple-400">Month Revenue</p>
                      <p className="text-xl font-bold text-purple-800 dark:text-purple-300">
                        {formatCurrency(lubricantsData?.month?.revenue)}
                      </p>
                    </div>
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <p className="text-xs text-orange-700 dark:text-orange-400">Month Profit</p>
                      <p className="text-xl font-bold text-orange-800 dark:text-orange-300">
                        {formatCurrency(lubricantsData?.month?.profit)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Low Stock & Out of Stock Alerts */}
                  {(lubricantsData?.out_of_stock?.length > 0 || lubricantsData?.low_stock_alerts?.length > 0) && (
                    <div className="mt-4 pt-4 border-t space-y-2">
                      {lubricantsData?.out_of_stock?.length > 0 && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium mb-2">
                            <AlertTriangle className="w-4 h-4" />
                            Out of Stock ({lubricantsData.out_of_stock.length})
                          </div>
                          <div className="space-y-1">
                            {lubricantsData.out_of_stock.slice(0, 3).map((p, i) => (
                              <p key={i} className="text-xs text-red-600">{p.name} ({p.brand})</p>
                            ))}
                            {lubricantsData.out_of_stock.length > 3 && (
                              <p className="text-xs text-red-500">+{lubricantsData.out_of_stock.length - 3} more</p>
                            )}
                          </div>
                        </div>
                      )}
                      {lubricantsData?.low_stock_alerts?.length > 0 && (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium mb-2">
                            <AlertTriangle className="w-4 h-4" />
                            Low Stock ({lubricantsData.low_stock_alerts.length})
                          </div>
                          <div className="space-y-1">
                            {lubricantsData.low_stock_alerts.slice(0, 3).map((p, i) => (
                              <p key={i} className="text-xs text-amber-600">{p.name}: {p.stock} left (min: {p.min_alert})</p>
                            ))}
                            {lubricantsData.low_stock_alerts.length > 3 && (
                              <p className="text-xs text-amber-500">+{lubricantsData.low_stock_alerts.length - 3} more</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}
            </div>

            {/* Lubricants Charts Row */}
            {settings?.lubricants?.enabled && lubricantsData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Inventory vs Sales Stacked Chart */}
                <Card className="p-6">
                  <h3 className="font-semibold flex items-center gap-2 mb-4">
                    <Package className="w-5 h-5 text-purple-500" />
                    Inventory Cost vs Sales (6 Months)
                  </h3>
                  {lubricantsData?.inventory_sales_chart?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={lubricantsData.inventory_sales_chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 11 }} 
                          tickFormatter={(v) => {
                            const [year, month] = v.split('-');
                            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return months[parseInt(month) - 1];
                          }}
                        />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                        <Tooltip 
                          formatter={(value, name) => [formatCurrency(value), name === 'inventory_cost' ? 'Inventory Cost' : name === 'sales' ? 'Sales' : 'Profit']}
                          labelFormatter={(label) => {
                            const [year, month] = label.split('-');
                            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                            return `${months[parseInt(month) - 1]} ${year}`;
                          }}
                        />
                        <Legend />
                        <Bar dataKey="inventory_cost" name="Inventory Cost" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="sales" name="Sales" stackId="a" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-muted">No data available</div>
                  )}
                </Card>

                {/* Inventory Aging by Expiry */}
                <Card className="p-6">
                  <h3 className="font-semibold flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Inventory Aging (By Expiry)
                  </h3>
                  {lubricantsData?.expiry_summary ? (
                    <>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart 
                          data={[
                            { name: 'Expired', value: lubricantsData.expiry_summary.expired, fill: '#ef4444' },
                            { name: '< 30 days', value: lubricantsData.expiry_summary.within_30_days, fill: '#f97316' },
                            { name: '30-60 days', value: lubricantsData.expiry_summary.within_60_days, fill: '#eab308' },
                            { name: '60-90 days', value: lubricantsData.expiry_summary.within_90_days, fill: '#84cc16' },
                            { name: 'Healthy', value: lubricantsData.expiry_summary.healthy, fill: '#22c55e' }
                          ]}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={70} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {[
                              { fill: '#ef4444' },
                              { fill: '#f97316' },
                              { fill: '#eab308' },
                              { fill: '#84cc16' },
                              { fill: '#22c55e' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-red-500"></span>
                          <span>Expired: {formatCurrency(lubricantsData.expiry_summary.expired)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-orange-500"></span>
                          <span>&lt;30d: {formatCurrency(lubricantsData.expiry_summary.within_30_days)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-yellow-500"></span>
                          <span>30-60d: {formatCurrency(lubricantsData.expiry_summary.within_60_days)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-green-500"></span>
                          <span>Healthy: {formatCurrency(lubricantsData.expiry_summary.healthy)}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-muted">No expiry data available</div>
                  )}
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Dashboard;
